import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getLatestDate, getMinDate, getPriceForDate } from '../services/metalPriceService';
import { calculateNisab } from '../utils/calculations';
import { NisabCalculation } from '../types';

const Nisab: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [nisabDate, setNisabDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [minDate] = useState(getMinDate());
  const [nisabCalc, setNisabCalc] = useState<NisabCalculation | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      const latest = await getLatestDate();
      setMaxDate(latest);
      const dateParam = searchParams.get('nisabDate') || latest;
      setNisabDate(dateParam);
    };
    init();
  }, [searchParams]);

  const handleCalculate = async () => {
    if (!nisabDate) return;
    setLoading(true);
    const prices = await getPriceForDate(nisabDate);
    if (prices) {
      const calc = calculateNisab(prices.gold, prices.silver);
      setNisabCalc(calc);
    }
    setLoading(false);
  };

  return (
    <div className="container mt-5">
      <h2>Nisab Calculator</h2>
      <div className="card mt-4">
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Select Date</label>
            <input
              type="date"
              className="form-control"
              value={nisabDate}
              min={minDate}
              max={maxDate}
              onChange={(e) => setNisabDate(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={handleCalculate} disabled={loading}>
            {loading ? 'Calculating...' : 'Calculate Nisab'}
          </button>
        </div>
      </div>

      {nisabCalc && (
        <div className="card mt-4">
          <div className="card-header">
            <h4>Nisab for {new Date(nisabDate).toLocaleDateString()}</h4>
          </div>
          <div className="card-body">
            <h5>Gold Nisab</h5>
            <table className="table">
              <thead>
                <tr>
                  <th>Scholar</th>
                  <th>Weight (g)</th>
                  <th>Nisab (£)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Shaykh Samer Al-Nass</td>
                  <td>{nisabCalc.goldShSamer.weight}</td>
                  <td>£{nisabCalc.goldShSamer.nisab.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Subcontinent Scholars</td>
                  <td>{nisabCalc.goldSubScholars.weight}</td>
                  <td>£{nisabCalc.goldSubScholars.nisab.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Shaykh Mazin Bakir</td>
                  <td>{nisabCalc.goldShMazin.weight}</td>
                  <td>£{nisabCalc.goldShMazin.nisab.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            <h5 className="mt-4">Silver Nisab</h5>
            <table className="table">
              <thead>
                <tr>
                  <th>Scholar</th>
                  <th>Weight (g)</th>
                  <th>Nisab (£)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Shaykh Samer Al-Nass</td>
                  <td>{nisabCalc.silverShSamer.weight}</td>
                  <td>£{nisabCalc.silverShSamer.nisab.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Subcontinent Scholars</td>
                  <td>{nisabCalc.silverSubScholars.weight}</td>
                  <td>£{nisabCalc.silverSubScholars.nisab.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Shaykh Mazin Bakir</td>
                  <td>{nisabCalc.silverShMazin.weight}</td>
                  <td>£{nisabCalc.silverShMazin.nisab.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Nisab;
